<script setup>
import { ref, computed } from "vue"
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import SVGIcon from '@/components/SVGIcon.vue';

const dataConnect = new DatastoreConnect();

const props = defineProps({
  field: {
    type: Object,
    default: () => ({}),
  },
  parent: {
    type: String,
    default: '',
  },
});

const files = ref([])
const uploading = ref(false)
const progress = ref({}) // name -> %
const selectFolder=ref(false);
const loading_status=ref('select');
const dropHighlighted = ref(false);
const dragCounter = ref(0);
var rootFolder = "";

const emit = defineEmits(['update:modelValue']);

const acceptFilter = computed(() => {
  const filter = props.field?.files_filter;
  return Array.isArray(filter) && filter.length > 0 ? filter.join(',') : null;
});

const select_msg = computed(() => {

  if (loading_status.value=='select'){
    if (files.value.length>0) return "";
    if (selectFolder.value) return "Select folder";
    return "Select files";
  }
  if (loading_status.value=='loading'){
    return "Uploading files";
  }
  if (loading_status.value=='loaded'){
    return "Uploading complete";
  }
  return "Uploading error";

});

/* =====================
   File selection
===================== */
function filterByExtension(fileList) {
  const filter = props.field?.files_filter;
  if (Array.isArray(filter) && filter.length > 0) {
    const extensions = filter.map((ext) => String(ext).toLowerCase());
    return fileList.filter((f) => extensions.some((ext) => f.name.toLowerCase().endsWith(ext)));
  }
  return fileList;
}

function onFilesSelected(e) {
  loading_status.value='select';
  // `accept` is ignored by browsers when selecting a folder (webkitdirectory), so re-filter here.
  files.value = filterByExtension(Array.from(e.target.files));
}

function removeFile(file) {
  files.value = files.value.filter((f) => f !== file);
  delete progress.value[file.name];
}

/* =====================
   Drag and drop
===================== */
function handleDragEnter() {
  dragCounter.value += 1;
  dropHighlighted.value = true;
}

function handleDragLeave() {
  dragCounter.value -= 1;
  if (dragCounter.value <= 0) {
    dragCounter.value = 0;
    dropHighlighted.value = false;
  }
}

// reads a dropped directory entry's contents, paginating via readEntries
function readAllDirectoryEntries(dirReader) {
  return new Promise((resolve, reject) => {
    const allEntries = [];
    const readBatch = () => {
      dirReader.readEntries((entries) => {
        if (!entries.length) {
          resolve(allEntries);
          return;
        }
        allEntries.push(...entries);
        readBatch();
      }, reject);
    };
    readBatch();
  });
}

// recursively walks a dropped file/directory entry, tagging files with their relative path
async function traverseEntry(entry, path = '') {
  if (!entry) return [];

  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
    const relativePath = `${path}${entry.name}`;
    try {
      Object.defineProperty(file, 'webkitRelativePath', { value: relativePath, writable: false, configurable: true });
    } catch (e) {
      file.relativePath = relativePath;
    }
    return [file];
  }

  if (entry.isDirectory) {
    const dirReader = entry.createReader();
    const entries = await readAllDirectoryEntries(dirReader);
    const nested = await Promise.all(entries.map((child) => traverseEntry(child, `${path}${entry.name}/`)));
    return nested.flat();
  }

  return [];
}

async function handleFileDrop(e) {
  dropHighlighted.value = false;
  dragCounter.value = 0;

  const items = e.dataTransfer?.items ? [...e.dataTransfer.items] : [];
  const entries = items
    .map((item) => (typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null))
    .filter(Boolean);

  let dropped = [];
  if (entries.length > 0) {
    const nested = await Promise.all(entries.map((entry) => traverseEntry(entry)));
    dropped = nested.flat();
  } else if (e.dataTransfer?.files) {
    dropped = Array.from(e.dataTransfer.files);
  }

  loading_status.value = 'select';
  const newFiles = filterByExtension(dropped);
  const existingKeys = new Set(files.value.map((f) => `${f.webkitRelativePath || ''}/${f.name}`));
  const uniqueNewFiles = newFiles.filter((f) => !existingKeys.has(`${f.webkitRelativePath || ''}/${f.name}`));
  files.value = [...files.value, ...uniqueNewFiles];
}

/* =====================
   Upload entry point
===================== */
async function uploadAll() {
  if (!files.value.length) return
  uploading.value = true
  loading_status.value='loading';

  // 1️⃣ Ask backend how to upload each file
  
  let ff= Array.from(files.value).map(f => ({
    name: f.name,
    path: f.webkitRelativePath,
    size: f.size,
    type: f.type,
  }))
  const resp = await dataConnect.uploadGetUrl({files:ff,folder:rootFolder});

  rootFolder=resp.result["folder"]
  const uploads  = resp.result["urls"];

  // 2️⃣ Upload each file
  for (const upload of uploads) {
    let file='';
    if (upload.path!=''){
      file = files.value.find(f => f.webkitRelativePath === upload.path)
    }
    if (!file) {
      file = files.value.find(f => f.name === upload.path)
    }
    if (!file) continue;

    progress.value[file.name] = 0

    if (upload.strategy === "single") {
      await uploadSingle(file, upload)
    } else {
      await uploadMultipart(file, upload)
    }

    progress.value[file.name] = 100
  }

  uploading.value = false
}

/* =====================
   Single PUT upload
===================== */
async function uploadSingle(file, plan) {
  try {
    let resp = await fetch(plan.putUrl, {method: "PUT",body: file});
    //console.log(resp);
    loading_status.value='loaded';
     emit('update:modelValue', rootFolder);
  }
  catch(ee) {
    console.log(ee);
    loading_status.value='error';
  }
  
}

/* =====================
   Multipart upload
===================== */
async function uploadMultipart(file, uploadObj) {
  const etags = []
  const totalParts = uploadObj.parts.length
  const partSize = uploadObj.partSize || uploadObj.part_size

  for (let i = 0; i < uploadObj.parts.length; i++) {
    const part = uploadObj.parts[i]
    const partNumber = part.partNumber || part.part_number || part.PartNumber || i + 1
    const partUrl = part.url || part.putUrl || part.put_url
    const start = (partNumber - 1) * partSize
    const end = Math.min(start + partSize, file.size)

    const chunk = file.slice(start, end)

    const res = await fetch(partUrl, {
      method: "PUT",
      body: chunk
    })

    if (!res.ok) {
      throw new Error(`Failed to upload part ${partNumber}: ${res.status} ${res.statusText}`)
    }

    const etag = res.headers.get("ETag")

    etags.push({
      PartNumber: partNumber,
      ETag: etag
    })

    progress.value[file.name] = Math.round(
      ((i + 1) / totalParts) * 100
    )
  }

  // 3️⃣ Notify backend to complete upload
  let params= {
    key: uploadObj.key,
    upload_id: uploadObj.uploadId || uploadObj.upload_id,
    etag_list:etags,
    mode:'app-temp'
  }
  const resp = await dataConnect.completeSnapshotUploadMultipartUrl(params);
  if (resp.error) {
    loading_status.value='error';
  } else {
    loading_status.value='loaded';
    emit('update:modelValue', rootFolder);
  }

}
</script>

<template>
  <div class="uploader">
    <label v-if="props.field?.label || props.field?.description" class="label-spacing">
      {{ props.field.label || props.field.name }}
      <SVGIcon
        v-if="props.field.description"
        v-tooltip="props.field.description"
        :iconName="'info-square'"
        class="info-icon"
      />
    </label>

    <div
      class="drop-zone"
      :class="{ '_highlight': dropHighlighted }"
      @dragenter.prevent="handleDragEnter"
      @dragover.prevent=""
      @dragleave="handleDragLeave"
      @drop.stop.prevent="handleFileDrop"
    >
      <div class="top-row">
        <input v-if="selectFolder"
          type="file"
          multiple
          webkitdirectory
          directory
          :accept="acceptFilter"
          @change="onFilesSelected"
        />
        <input v-else
          type="file"
          multiple
          :accept="acceptFilter"
          @change="onFilesSelected"
        />
        <button class="button button-sm" @click="uploadAll" :disabled="uploading" v-if="files.length>0">
          Upload
        </button>
      </div>
      <span class="drop-zone__hint">Drag and drop files or a folder here</span>
    </div>

    <div class="top-row select-row">
      <label style="width: 200px;">
        <input type="checkbox" v-model="selectFolder"/>
        Select folder
      </label>
      <div :class="loading_status">{{ select_msg }}</div>
    </div>

    
    <hr>
    <div class="file-list">
      <div v-for="file in files" :key="file.name" class="progress">
        <SVGIcon
          :iconName="'cancel_filled'"
          :width="16"
          :height="16"
          class="remove-icon"
          @click="removeFile(file)"
        />
        <span>{{ file.name }}</span>
        <progress :value="progress[file.name] || 0" max="100"></progress>
        <span>{{ progress[file.name] || 0 }}%</span>
      </div>
    </div>

   

  </div>
</template>

<style scoped>
.label-spacing {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  margin-top: 6px;
  margin-bottom: 4px;
}
.drop-zone {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  padding: 10px;
  border: 2px dashed #ccc;
  border-radius: 6px;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.drop-zone._highlight {
  border-color: #250e81;
  background-color: rgba(37, 14, 129, 0.06);
}
.drop-zone__hint {
  font-size: 0.8rem;
  color: #777;
}
.top-row {
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
}
.select-row {
  justify-content: flex-start;
  gap: 10px;
}
.file-list {
  overflow-y: auto;
  max-height: 200px;
  color: black;
}
.uploader {
  width: 100%;
}
.progress {
  display: flex;
  gap: 10px;
  align-items: center;
}
.remove-icon {
  cursor: pointer;
  flex-shrink: 0;
}
progress {
  width: 200px;
}

.error{
  color: rgb(139, 0, 0);
}
.loaded{
  color: rgb(2, 126, 4);
}
.loading{
  color: rgb(134, 164, 2);
}
.select{
  color: rgb(2, 68, 115);
}
</style>
